import { isNotNil } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgCatalogueItem } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  GridAlignmentType,
  gridColDefFactory,
  NULLISH_CELL_VALUE,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { ImageCell } from '~/components/DataGridCustom/cells/ImageCell/ImageCell';

import { ProductCell } from './components/ProductCell/ProductCell';
import { SelectCell } from './components/SelectCell/SelectCell';

export const useCatalogueSearchResultDataGrid: UseDataGridConfig<
  SvbrgCatalogueItem,
  {
    // Optional checkId to link selection to a form field
    checkId?: string;
  }
> = ({ checkId }) => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.intake, { keyPrefix: 'CatalogueSearchResultDataGrid.column' });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });

    const config = {
      sortModel: [],
      columnVisibilityModel: {},
      columns: [
        createColumn({
          field: 'previewImage',
          minWidth: 100,
          align: GridAlignmentType.LEFT,
          hideable: false,
          sortable: false,
          renderCell: cellFactory(ImageCell, ({ row, value }) => ({ alt: row.name, src: value })),
        }),
        createColumn({
          field: 'name',
          headerName: tColumn('product'),
          minWidth: 532,
          align: GridAlignmentType.LEFT,
          hideable: false,
          sortable: false,
          renderCell: cellFactory(ProductCell),
        }),
        createColumn({
          field: 'price',
          minWidth: 120,
          align: GridAlignmentType.RIGHT,
          hideable: false,
          sortable: false,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({ currency: row.currency, value: row.price })),
        }),
        createColumn({
          field: 'inStock',
          headerName: tColumn('availability'),
          minWidth: 120,
          align: GridAlignmentType.LEFT,
          hideable: false,
          sortable: false,
          valueFormatter: (value) => {
            return value ? tColumn('inStock') : tColumn('outOfStock');
          },
        }),
        createColumn({
          field: 'deliveryTime',
          minWidth: 120,
          align: GridAlignmentType.LEFT,
          hideable: false,
          sortable: false,
          valueFormatter: (value) => {
            return value ? tColumn('deliveryTimeInDays', { days: value }) : NULLISH_CELL_VALUE;
          },
        }),
        createColumn({
          field: 'shippingCosts',
          minWidth: 120,
          align: GridAlignmentType.RIGHT,
          hideable: false,
          sortable: false,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({ currency: row.currency, value: row.shippingCosts })),
        }),
      ],
    } as DataGridConfig<SvbrgCatalogueItem>;

    if (isNotNil(checkId)) {
      config.columns.push(
        createColumn({
          field: 'select',
          headerName: tColumn('select'),
          minWidth: 150,
          align: GridAlignmentType.CENTER,
          hideable: false,
          sortable: false,
          renderCell: cellFactory(SelectCell, ({ row }) => ({ item: row, value: checkId, prefix: 'items' })),
        }),
      );
    }

    return config;
  }, [tColumn, t, checkId]);
};
